import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import I18n from "i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CLIENT_VERSION_CHECK_INTERVAL,
  fetchPublishedClientVersion,
  isNewerVersion,
} from "./clientVersion";

export default function ClientUpdateBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const lastCheckRef = useRef(0);
  const checkInProgressRef = useRef(false);
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("sm"));

  const checkVersion = useCallback(async () => {
    if (checkInProgressRef.current) {
      return;
    }

    checkInProgressRef.current = true;
    lastCheckRef.current = Date.now();
    try {
      const publishedVersion = await fetchPublishedClientVersion();
      if (publishedVersion && isNewerVersion(publishedVersion)) {
        setUpdateAvailable(true);
      }
    } catch (error) {
      console.error("Failed to check the published client version:", error);
    } finally {
      checkInProgressRef.current = false;
    }
  }, []);

  useEffect(() => {
    checkVersion();
    const intervalId = window.setInterval(
      checkVersion,
      CLIENT_VERSION_CHECK_INTERVAL,
    );
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastCheckRef.current >= CLIENT_VERSION_CHECK_INTERVAL
      ) {
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkVersion]);

  if (!updateAvailable) {
    return null;
  }

  return (
    <Alert
      severity="info"
      variant="filled"
      sx={{
        borderRadius: 0,
        mx: mdDown ? 0 : -3,
        alignItems: "center",
        "& .MuiAlert-message": { flex: 1 },
      }}
      action={
        <Button
          color="inherit"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => window.location.reload()}
        >
          {I18n.t("ClientUpdate.reload")}
        </Button>
      }
    >
      <Typography variant="body2" fontWeight="bold">
        {I18n.t("ClientUpdate.message")}
      </Typography>
    </Alert>
  );
}
